import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  LogIn,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Settings,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { DynamicLogo } from '../../components/DynamicLogo';
import { ThemeToggle } from '../../components/ThemeToggle';

export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();
  const hasCheckedAuth = useRef(false);

  const { login, logout, isLoading, error, isAuthenticated, isAdmin, hasHydrated, clearError } = useAuth();

  useEffect(() => {
    if (!hasHydrated || hasCheckedAuth.current) return;
    hasCheckedAuth.current = true;

    if (isAuthenticated) {
      if (isAdmin) {
        navigate('/dashboard', { replace: true });
      } else {
        logout();
        setIsReady(true);
      }
    } else {
      setIsReady(true);
    }
  }, [hasHydrated, isAuthenticated, isAdmin, logout, navigate]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    try {
      const result = await login(email, password);

      if (result?.user?.role === 'admin') {
        navigate('/dashboard', { replace: true });
      } else {
        logout();
        setLoginError('Acesso restrito a administradores');
      }
    } catch {
      // Erro já tratado no store
    }
  };

  const displayError = loginError || error;

  if (!isReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Área Administrativa | Portal</title>
        <meta name="description" content="Acesso restrito para administradores do sistema." />
      </Helmet>

      <div className="min-h-screen bg-background flex items-center justify-center py-20 px-4">
        {/* Theme Toggle */}
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>

        {/* Background decorativo */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-brand/5 rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md relative z-10"
        >
          <div className="p-8 rounded-2xl bg-card border border-border shadow-lg">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <DynamicLogo
                  area="login"
                  alt="Admin Portal"
                  className="h-16 w-auto"
                  fallback={
                    <div className="inline-flex p-4 rounded-xl bg-foreground">
                      <Settings className="w-10 h-10 text-background" />
                    </div>
                  }
                />
              </div>
              <h1 className="text-2xl font-bold">Área Administrativa</h1>
              <p className="text-muted-foreground text-sm mt-2">
                Acesso restrito para administradores
              </p>
            </div>

            {displayError && (
              <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/30 flex items-center gap-2 text-destructive">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{displayError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="admin-email" className="label mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  id="admin-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="admin@portal.com"
                  className="input"
                />
              </div>

              <div>
                <label htmlFor="admin-password" className="label mb-2 block">
                  Senha
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="admin-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="••••••••"
                    className="input pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-brand text-white font-semibold rounded-lg hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    Entrar
                    <LogIn className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center gap-2 text-muted-foreground text-xs justify-center">
                <Shield className="w-4 h-4" />
                <span>Conexão segura e criptografada</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
